import { useEffect, useId, useState } from 'react';

import { PIEZAS, type Pieza } from './piezas';

/**
 * El escudo del logo apareciendo: chispa, llama, gorro, cara y respiración.
 *
 * Es el mismo que abre el POS (`app_logo_reveal.dart`), y los dos salen de
 * `design-reference/Logo Animado.html`: los tiempos y las curvas de abajo están
 * copiados uno a uno. Aquí va entero en un solo `<svg>`, con las piezas dentro,
 * así que cada movimiento se escribe en las unidades del dibujo y no hay que
 * convertir nada a píxeles: se ve igual a cualquier tamaño.
 *
 * Corre **una vez y se queda quieta**, como el original. Con «reducir
 * movimiento» activado en el equipo, aparece ya terminada.
 */
export function LogoAnimado({ className = '', halo = true }: { className?: string; halo?: boolean }) {
  const t = useReloj(DURACION);
  const recorte = useId().replace(/:/g, '');

  // Dos senos que no comparten periodo: la llama nunca repite el mismo gesto.
  const titileo = Math.sin(t * 5.3) * 0.62 + Math.sin(t * 8.9 + 1.1) * 0.38;
  const viva = acota((t - 0.9) / 0.5, 0, 1) * FLICKER;

  // La cámara: siempre hay algo moviéndose, aunque la escena esté quieta.
  const camaraEscala = entra(1.075, 1.0, 0, 5.0, t);
  const camaraY = deLaEscena(entra(16, 0, 0, 5.0, t));
  const respira = 1 + 0.005 * Math.sin(t * 2.4) + asienta(t, 2.95, -0.014, 20, 11);

  // ── Chispa ──────────────────────────────────────────────────────────────
  const chispaY = entra(120, -40, 0.02, 0.62, t);
  const chispaOpacidad = t < 0.04 ? 0 : t < 0.4 ? 1 : Math.max(0, 1 - (t - 0.4) / 0.26);
  const chispaRadio = entra(16, 4, 0.06, 0.68, t);

  // ── El contorno violeta se revela de abajo arriba ───────────────────────
  const corte = entra(742, 195, 0.26, 1.15, t);
  const subida = entra(26, 0, 0.28, 1.35, t);
  const contornoEscala = rebota(0.5, 1, 0.26, 1.25, t);

  // ── El cuerpo blanco crece desde la base ────────────────────────────────
  const cuerpoY = rebota(0.18, 1, 1.0, 1.8, t);
  const cuerpoX = rebota(0.62, 1, 1.0, 1.85, t);
  const cuerpoOpacidad = entra(0, 1, 0.95, 1.2, t);
  const brazosOpacidad = entra(0, 1, 1.5, 2.0, t);

  // ── El gorro cae, aterriza y asienta ────────────────────────────────────
  const caida = entra(-700, 0, 2.16, 2.95, t, (x) => x * x);
  const gorroY = caida + asienta(t, 2.95, 16, 19, 8.5);
  const gorroGiro = entra(-9, 0, 2.18, 2.9, t) + asienta(t, 2.95, 3.2, 17, 7.5);
  const gorroAplastado = 1 + asienta(t, 2.95, -0.075, 21, 10.5);
  const hayImpacto = t > 2.95 && t < 3.6;
  const impactoRadio = entra(30, 210, 2.95, 3.55, t);
  const impactoOpacidad = hayImpacto ? Math.max(0, 0.3 * (1 - (t - 2.95) / 0.6)) : 0;

  // ── La cara ─────────────────────────────────────────────────────────────
  const ojoIzquierdo = rebota(0, 1, 3.2, 3.6, t);
  const ojoDerecho = rebota(0, 1, 3.3, 3.7, t);
  // Un pestañeo, a los cuatro segundos y medio, que dura un suspiro.
  const pestaneo = Math.max(0, 1 - Math.abs(t - 4.5) / 0.085);
  const parpado = 1 - 0.9 * pestaneo;
  const hocico = rebota(0, 1, 3.42, 3.95, t);
  const bigote = rebota(0.2, 1, 3.58, 4.05, t);

  const tv = titileo * viva;
  const llama =
    `translate(${BASE_X} ${BASE_Y}) translate(${1.8 * tv} ${subida}) skewX(${-0.6 * tv}) ` +
    `scale(${1 - 0.006 * tv} ${1 + 0.012 * tv}) translate(${-BASE_X} ${-BASE_Y})`;
  const gorro =
    `translate(${BASE_X} 470) scale(${1 / gorroAplastado} ${gorroAplastado}) translate(${-BASE_X} -470) ` +
    `translate(0 ${gorroY}) rotate(${gorroGiro} ${BASE_X} 445)`;

  return (
    <div className={`logo-animado ${className}`} aria-hidden="true">
      {halo ? (
        <div
          className="logo-animado__halo"
          style={{
            opacity: acota(acota((t - 0.15) / 0.8, 0, 1) * (0.5 + 0.16 * titileo), 0, 1),
            transform: `scale(${1 + 0.03 * titileo})`,
          }}
        />
      ) : null}
      <svg viewBox={`${VIEW_X} ${VIEW_Y} ${VIEW_ANCHO} ${VIEW_ALTO}`} overflow="visible">
        <defs>
          {/*
           * El revelado: una banda inclinada que descubre la llama de abajo
           * arriba. Torcida siete grados a propósito: recta, el contorno
           * aparecía como una persiana subiendo; torcida, parece que prende.
           */}
          <clipPath id={recorte} clipPathUnits="userSpaceOnUse">
            <rect x={60} y={corte} width={580} height={900 - corte} transform={`rotate(-7 ${BASE_X} ${corte})`} />
          </clipPath>
        </defs>

        <g transform={sobre(CENTRO_X, CENTRO_Y, `translate(0 ${camaraY}) scale(${camaraEscala})`)}>
          {/* El original respira desde el 80 % del alto: se hincha hacia arriba. */}
          <g transform={sobre(CENTRO_X, VIEW_Y + VIEW_ALTO * 0.8, `scale(${respira})`)}>
            <g opacity={acota(chispaOpacidad, 0, 1)}>
              <circle cx={BASE_X} cy={BASE_Y + chispaY} r={chispaRadio * 3.2} className="logo-animado__chispa-halo" />
              <circle cx={BASE_X} cy={BASE_Y + chispaY} r={chispaRadio} className="logo-animado__tinta" />
            </g>

            <g transform={llama}>
              <g clipPath={`url(#${recorte})`}>
                <g transform={sobre(BASE_X, BASE_Y, `scale(${contornoEscala})`)}>
                  <Pieza nombre="fuego" />
                  <g
                    opacity={acota(cuerpoOpacidad, 0, 1)}
                    transform={sobre(BASE_X, BASE_Y, `scale(${cuerpoX} ${cuerpoY})`)}
                  >
                    <Pieza nombre="cuerpo" />
                    <g opacity={acota(brazosOpacidad, 0, 1)}>
                      <Pieza nombre="brazos" />
                    </g>
                  </g>
                </g>
              </g>

              {t >= 2.16 ? (
                <g transform={gorro}>
                  <Pieza nombre="gorro" />
                </g>
              ) : null}

              {impactoOpacidad > 0 ? (
                <circle
                  cx={BASE_X}
                  cy={468}
                  r={impactoRadio}
                  fill="none"
                  strokeWidth={4}
                  opacity={acota(impactoOpacidad, 0, 1)}
                  className="logo-animado__trazo"
                />
              ) : null}

              <g transform={sobre(407.64, 575.7, `scale(${ojoIzquierdo} ${ojoIzquierdo * parpado})`)}>
                <Pieza nombre="ojo-izquierdo" />
              </g>
              <g transform={sobre(269.53, 576.19, `scale(${ojoDerecho} ${ojoDerecho * parpado})`)}>
                <Pieza nombre="ojo-derecho" />
              </g>
              <g transform={sobre(339.5, 640, `scale(${hocico})`)}>
                <Pieza nombre="hocico" />
                <g transform={sobre(340, 612, `scale(${bigote})`)}>
                  <Pieza nombre="bigote" />
                </g>
              </g>
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
}

/** Una pieza del dibujo, tal cual sale de sus SVG. Son archivos del repositorio. */
function Pieza({ nombre }: { nombre: Pieza }) {
  return <g dangerouslySetInnerHTML={{ __html: PIEZAS[nombre] }} />;
}

// ── Geometría, en las unidades del dibujo: el viewBox de `marca.svg` ────────
const VIEW_X = 138.57;
const VIEW_Y = 219.38;
const VIEW_ANCHO = 409.85;
const VIEW_ALTO = 503.49;
const CENTRO_X = VIEW_X + VIEW_ANCHO / 2;
const CENTRO_Y = VIEW_Y + VIEW_ALTO / 2;

/** La base de la llama: casi todo gira, crece o se inclina alrededor de aquí. */
const BASE_X = 343;
const BASE_Y = 722;

/** Los cinco segundos de las cinco escenas del original. */
const DURACION = 5;

/** Cuánto ondula la llama: el mismo mando que el original. */
const FLICKER = 1.2;

/**
 * Píxeles de la escena original, en unidades del dibujo. Aquella dibujaba el
 * escudo a 660 px de alto, y un par de medidas —el acercamiento de la cámara—
 * están en sus píxeles.
 */
function deLaEscena(pixeles: number): number {
  return (pixeles * VIEW_ALTO) / 660;
}

/** Una transformación alrededor de un punto, como el `about()` del original. */
function sobre(x: number, y: number, operacion: string): string {
  return `translate(${x} ${y}) ${operacion} translate(${-x} ${-y})`;
}

/** Los segundos desde que se montó, hasta `hasta`. Luego se queda quieto. */
function useReloj(hasta: number): number {
  const [t, setT] = useState(() => (prefiereQuieto() ? hasta : 0));

  useEffect(() => {
    if (prefiereQuieto()) return;

    let cuadro = 0;
    const inicio = performance.now();
    const avanzar = (ahora: number) => {
      const segundos = Math.min((ahora - inicio) / 1000, hasta);
      setT(segundos);
      if (segundos < hasta) cuadro = requestAnimationFrame(avanzar);
    };
    cuadro = requestAnimationFrame(avanzar);

    return () => cancelAnimationFrame(cuadro);
  }, [hasta]);

  return t;
}

function prefiereQuieto(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

// ── Las tres ayudas de movimiento del original ─────────────────────────────

function entra(
  desde: number,
  hasta: number,
  inicio: number,
  fin: number,
  tiempo: number,
  curva: (x: number) => number = suaveCubica,
): number {
  if (tiempo <= inicio) return desde;
  if (tiempo >= fin) return hasta;
  return desde + (hasta - desde) * curva((tiempo - inicio) / (fin - inicio));
}

function rebota(desde: number, hasta: number, inicio: number, fin: number, tiempo: number): number {
  return entra(desde, hasta, inicio, fin, tiempo, suaveAtras);
}

/** La oscilación amortiguada de algo que acaba de aterrizar. */
function asienta(tiempo: number, desde: number, amplitud: number, frecuencia: number, amortiguacion: number): number {
  if (tiempo < desde) return 0;
  const pasado = tiempo - desde;
  return amplitud * Math.exp(-pasado * amortiguacion) * Math.sin(pasado * frecuencia);
}

function suaveCubica(x: number): number {
  const u = x - 1;
  return u * u * u + 1;
}

/** La que se pasa de largo y vuelve. Es lo que hace que el gorro «caiga». */
function suaveAtras(x: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  const u = x - 1;
  return 1 + c3 * u * u * u + c1 * u * u;
}

function acota(valor: number, minimo: number, maximo: number): number {
  return valor < minimo ? minimo : valor > maximo ? maximo : valor;
}
